"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { useSearchParams } from "next/navigation"
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  Folder,
  FolderInput,
  FolderMinus,
  FolderOpen,
  FolderPlus,
  GitBranch,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  SquarePen,
  Trash2,
} from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { type ChatProject, type DialogItem } from "@/lib/copilot-chat"
import { PROJECTS_COLLAPSED_LIMIT } from "@/lib/chat-projects"
import { useChatList } from "@/hooks/use-chat-list"
import { useSidebar } from "@/components/ui/sidebar"

/** Тип данных перетаскиваемого чата */
const DRAG_TYPE = "application/x-casper-dialog"
const RECENT_DROP_ID = "__recent__"

type Editing = { kind: "chat" | "project"; id: string } | null
type ProjectForm = { mode: "create"; moveDialogId?: string } | null

export function RecentChatsSidebar() {
  const searchParams = useSearchParams()
  const activeDialogId = searchParams.get("dialog")
  const { isMobile, setOpenMobile } = useSidebar()

  const [editing, setEditing] = useState<Editing>(null)
  const [projectForm, setProjectForm] = useState<ProjectForm>(null)
  const [projectToDelete, setProjectToDelete] = useState<ChatProject | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)

  const openDialog = useCallback((dialogId: string) => {
    // Закрываем мобильный сайдбар немедленно
    if (isMobile) setOpenMobile(false)
    if (dialogId === activeDialogId) return
    const url = new URL(window.location.href)
    url.searchParams.set("dialog", dialogId)
    window.history.pushState({}, "", url.toString())
    window.dispatchEvent(new CustomEvent("casper:load-dialog", { detail: { dialogId } }))
  }, [activeDialogId, isMobile, setOpenMobile])

  const onActiveDialogDeleted = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete("dialog")
    window.history.pushState({}, "", url.toString())
    window.dispatchEvent(new Event("casper:new-chat"))
  }, [])

  const {
    dialogs,
    hasFetched,
    busyId,
    groups,
    recent,
    visibleGroups,
    expanded,
    showAllProjects,
    toggleProject,
    toggleShowAll,
    startNewChatInProject: startNewChat,
    deleteChat: handleDelete,
    cloneChat: handleClone,
    moveChat: handleMove,
    renameChat,
    renameProject,
    createProject: handleCreateProject,
    deleteProject: handleDeleteProject,
  } = useChatList({ activeDialogId, openDialog, onActiveDialogDeleted })

  const startNewChatInProject = (project: ChatProject) => {
    if (isMobile) setOpenMobile(false)
    startNewChat(project)
  }

  const handleRename = (kind: "chat" | "project", id: string, value: string) => {
    setEditing(null)
    return kind === "chat" ? renameChat(id, value) : renameProject(id, value)
  }

  // ── Перетаскивание чата в папку ──
  const dropHandlers = (targetId: string) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      if (dropTarget !== targetId) setDropTarget(targetId)
    },
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
      setDropTarget((t) => (t === targetId ? null : t))
    },
    onDrop: (e: React.DragEvent) => {
      const dialogId = e.dataTransfer.getData(DRAG_TYPE)
      setDropTarget(null)
      if (!dialogId) return
      e.preventDefault()
      const dialog = dialogs.find((d) => d.dialogId === dialogId)
      if (dialog) void handleMove(dialog, targetId === RECENT_DROP_ID ? null : targetId)
    },
  })

  const renderChat = (dialog: DialogItem, nested: boolean) => (
    <ChatRow
      key={dialog.dialogId}
      dialog={dialog}
      nested={nested}
      active={dialog.dialogId === activeDialogId}
      busy={dialog.dialogId === busyId}
      editing={editing?.kind === "chat" && editing.id === dialog.dialogId}
      projects={groups.map((g) => g.project)}
      onOpen={() => openDialog(dialog.dialogId)}
      onStartRename={() => setEditing({ kind: "chat", id: dialog.dialogId })}
      onRename={(v) => handleRename("chat", dialog.dialogId, v)}
      onCancelRename={() => setEditing(null)}
      onClone={() => handleClone(dialog)}
      onMove={(projectId) => handleMove(dialog, projectId)}
      onMoveToNewProject={() => setProjectForm({ mode: "create", moveDialogId: dialog.dialogId })}
      onDelete={() => handleDelete(dialog.dialogId)}
      onDragEnd={() => setDropTarget(null)}
    />
  )

  // Скелетон: показываем до тех пор, пока не завершится первый fetch
  if (!hasFetched) {
    return (
      <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="px-4 py-1.5 shrink-0">
          <span className="text-xs font-medium text-sidebar-foreground/50">Недавние</span>
        </div>
        <div className="flex flex-col gap-1 px-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-0 flex-1 overflow-hidden" data-chat-sidebar>
      {/* viewportClassName="overflow-x-hidden" — предотвращает горизонтальное расширение
          элементов за пределы сайдбара (Radix внутри использует display:table) */}
      <ScrollArea className="flex-1 min-h-0 recent-chats-scroll" viewportClassName="overflow-x-hidden">
        <div className="flex flex-col w-full pb-2">
          <section className="flex flex-col w-full" data-projects-section>
            <div className="group/section flex items-center pl-4 pr-2 py-1.5">
              <span className="flex-1 text-xs font-medium text-sidebar-foreground/50">Проекты</span>
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center rounded-md text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                onClick={() => setProjectForm({ mode: "create" })}
                aria-label="Новый проект"
                title="Новый проект"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-col gap-0.5 px-2 w-full">
              {groups.length === 0 && (
                <button
                  type="button"
                  className="flex items-center gap-2 h-9 px-2 rounded-lg text-sm text-sidebar-foreground/70 hover:bg-sidebar-accent"
                  onClick={() => setProjectForm({ mode: "create" })}
                >
                  <FolderPlus className="h-4 w-4 shrink-0" />
                  Новый проект
                </button>
              )}
              {visibleGroups.map(({ project, dialogs: projectDialogs }) => {
                const isOpen = expanded.has(project.id)
                return (
                  <div key={project.id} className="flex flex-col w-full min-w-0" data-project-id={project.id}>
                    <ProjectRow
                      project={project}
                      count={projectDialogs.length}
                      open={isOpen}
                      busy={busyId === project.id}
                      dropActive={dropTarget === project.id}
                      editing={editing?.kind === "project" && editing.id === project.id}
                      onToggle={() => toggleProject(project.id)}
                      onNewChat={() => startNewChatInProject(project)}
                      onStartRename={() => setEditing({ kind: "project", id: project.id })}
                      onRename={(v) => handleRename("project", project.id, v)}
                      onCancelRename={() => setEditing(null)}
                      onDelete={() => setProjectToDelete(project)}
                      dropHandlers={dropHandlers(project.id)}
                    />
                    {isOpen && (
                      <div className="flex flex-col gap-0.5 w-full min-w-0">
                        {projectDialogs.length === 0 ? (
                          <div className="pl-8 pr-2 py-1.5 text-xs text-sidebar-foreground/45">
                            Пока нет чатов — перетащите чат сюда или создайте новый
                          </div>
                        ) : (
                          projectDialogs.map((d) => renderChat(d, true))
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              {groups.length > PROJECTS_COLLAPSED_LIMIT && (
                <button
                  type="button"
                  className="flex items-center gap-2 h-8 px-2 rounded-lg text-xs text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                  onClick={toggleShowAll}
                  aria-expanded={showAllProjects}
                  data-projects-toggle
                >
                  {showAllProjects ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  {showAllProjects ? "Свернуть" : `Показать все (${groups.length})`}                </button>
              )}
            </div>
          </section>

          {dialogs.length > 0 && (
          <section className="flex flex-col w-full mt-2" data-recent-section>
            <div
              className={cn(
                "mx-2 px-2 py-1.5 rounded-lg transition-colors",
                dropTarget === RECENT_DROP_ID && "bg-sidebar-accent ring-1 ring-sidebar-foreground/20"
              )}
              {...dropHandlers(RECENT_DROP_ID)}
              title="Перетащите чат сюда, чтобы убрать его из проекта"
            >
              <span className="text-xs font-medium text-sidebar-foreground/50">Недавние</span>
            </div>
            <div className="flex flex-col gap-0.5 px-2 w-full" {...dropHandlers(RECENT_DROP_ID)}>
              {recent.map((d) => renderChat(d, false))}
              {recent.length === 0 && dialogs.length > 0 && (
                <div className="px-2 py-1.5 text-xs text-sidebar-foreground/45">Все чаты разложены по проектам</div>
              )}
            </div>
          </section>
          )}
        </div>
      </ScrollArea>

      <ProjectFormDialog
        form={projectForm}
        onClose={() => setProjectForm(null)}
        onSubmit={(name) => handleCreateProject(name, projectForm?.moveDialogId)}
      />

      <AlertDialog open={!!projectToDelete} onOpenChange={(open) => { if (!open) setProjectToDelete(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить проект «{projectToDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Папка будет удалена, а её чаты сохранятся и переместятся в «Недавние».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => { if (projectToDelete) void handleDeleteProject(projectToDelete) }}
            >
              Удалить проект
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ── Строка чата ─────────────────────────────────────────────────────────────

interface ChatRowProps {
  dialog: DialogItem
  nested: boolean
  active: boolean
  busy: boolean
  editing: boolean
  projects: ChatProject[]
  onOpen: () => void
  onStartRename: () => void
  onRename: (value: string) => void
  onCancelRename: () => void
  onClone: () => void
  onMove: (projectId: string | null) => void
  onMoveToNewProject: () => void
  onDelete: () => void
  onDragEnd: () => void
}

function ChatRow(props: ChatRowProps) {
  const { dialog, nested, active, busy, editing, projects } = props
  const title = dialog.title || "Без названия"
  const origin = dialog.origin
  return (
    <div
      className={cn(
        "group/chat-item flex items-center w-full min-w-0 rounded-lg h-9 px-2 cursor-pointer",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "transition-colors duration-100",
        nested && "pl-8",
        active && "bg-[#0000000f]",
        busy && "opacity-50 pointer-events-none"
      )}
      onClick={() => { if (!editing) props.onOpen() }}
      title={origin ? `${title}\n${origin.kind === "branch" ? "Ветка" : "Копия"} чата «${origin.title}»` : title}
      draggable={!editing}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, dialog.dialogId)
        e.dataTransfer.effectAllowed = "move"
      }}
      onDragEnd={props.onDragEnd}
      data-dialog-id={dialog.dialogId}
    >
      {origin?.kind === "branch" && (
        <GitBranch className="mr-1.5 h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50" aria-label="Ветка" data-dialog-branch />
      )}
      {editing ? (
        <InlineRename initial={dialog.title} onSubmit={props.onRename} onCancel={props.onCancelRename} label="Название чата" />
      ) : (
        <span className="flex-1 min-w-0 truncate text-sm font-normal text-sidebar-foreground leading-none">{title}</span>
      )}
      {dialog.running && (
        <span className="ml-1 flex shrink-0 items-center text-sidebar-foreground/60" title="Агент работает" data-dialog-running>
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-label="Агент работает" />
        </span>
      )}

      {!editing && (
        // Три точки — появляются при наведении на всю строку, ширина 0 в обычном состоянии
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex items-center justify-center rounded-md h-7",
                "w-0 overflow-hidden ml-0",
                "group-hover/chat-item:w-8 group-hover/chat-item:ml-1",
                "data-[state=open]:w-8 data-[state=open]:ml-1",
                // Без наведения (сенсорный экран) кнопка видна всегда
                "[@media(hover:none)]:w-8 [@media(hover:none)]:ml-1",
                "text-sidebar-foreground/50 hover:text-sidebar-foreground",
                "outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none",
                "[&_svg]:shrink-0"
              )}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
              aria-label="Действия с чатом"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem className="gap-2" onSelect={props.onStartRename}>
              <Pencil className="h-3.5 w-3.5" />
              Переименовать
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onSelect={props.onClone}>
              <Copy className="h-3.5 w-3.5" />
              Клонировать
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="gap-2">
                <FolderInput className="h-3.5 w-3.5" />
                Переместить в проект
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56 max-h-80 overflow-y-auto">
                <DropdownMenuItem className="gap-2" onSelect={props.onMoveToNewProject}>
                  <FolderPlus className="h-3.5 w-3.5" />
                  Новый проект…
                </DropdownMenuItem>
                {projects.length > 0 && <DropdownMenuSeparator />}
                {projects.map((p) => (
                  <DropdownMenuItem key={p.id} className="gap-2" onSelect={() => props.onMove(p.id)}>
                    <Folder className="h-3.5 w-3.5" />
                    <span className="flex-1 min-w-0 truncate">{p.name}</span>
                    {dialog.projectId === p.id && <Check className="h-3.5 w-3.5" />}
                  </DropdownMenuItem>
                ))}
                {dialog.projectId && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="gap-2" onSelect={() => props.onMove(null)}>
                      <FolderMinus className="h-3.5 w-3.5" />
                      Убрать из проекта
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive gap-2" onSelect={props.onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
              Удалить чат
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

// ── Строка папки проекта ────────────────────────────────────────────────────

interface ProjectRowProps {
  project: ChatProject
  count: number
  open: boolean
  busy: boolean
  dropActive: boolean
  editing: boolean
  onToggle: () => void
  onNewChat: () => void
  onStartRename: () => void
  onRename: (value: string) => void
  onCancelRename: () => void
  onDelete: () => void
  dropHandlers: Pick<React.HTMLAttributes<HTMLDivElement>, "onDragOver" | "onDragLeave" | "onDrop">
}

function ProjectRow(props: ProjectRowProps) {
  const { project, count, open, busy, dropActive, editing } = props
  const Icon = open ? FolderOpen : Folder
  return (
    <div
      className={cn(
        "group/project flex items-center w-full min-w-0 rounded-lg h-9 px-2 cursor-pointer",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors duration-100",
        dropActive && "bg-sidebar-accent ring-1 ring-sidebar-foreground/25",
        busy && "opacity-50 pointer-events-none"
      )}
      onClick={() => { if (!editing) props.onToggle() }}
      title={project.name}
      aria-expanded={open}
      data-project-row
      {...props.dropHandlers}
    >
      <span className="relative mr-2 flex h-4 w-4 shrink-0 items-center justify-center text-sidebar-foreground/70">
        <Icon className="h-4 w-4 group-hover/project:opacity-0" />
        <ChevronRight className={cn("absolute h-4 w-4 opacity-0 group-hover/project:opacity-100 transition-transform", open && "rotate-90")} />
      </span>
      {editing ? (
        <InlineRename initial={project.name} onSubmit={props.onRename} onCancel={props.onCancelRename} label="Название проекта" />
      ) : (
        <span className="flex-1 min-w-0 truncate text-sm text-sidebar-foreground leading-none">{project.name}</span>
      )}
      {!editing && count > 0 && (
        <span className="ml-1 shrink-0 text-xs text-sidebar-foreground/45 group-hover/project:hidden [@media(hover:none)]:hidden">{count}</span>
      )}
      {!editing && (
        <>
          <button
            type="button"
            className="ml-1 hidden h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/50 hover:text-sidebar-foreground group-hover/project:flex [@media(hover:none)]:flex"
            onClick={(e) => { e.stopPropagation(); props.onNewChat() }}
            aria-label={`Новый чат в проекте «${project.name}»`}
            title="Новый чат в проекте"
          >
            <SquarePen className="h-3.5 w-3.5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "flex items-center justify-center rounded-md h-7 w-0 overflow-hidden",
                  "group-hover/project:w-7 data-[state=open]:w-7 [@media(hover:none)]:w-7",
                  "text-sidebar-foreground/50 hover:text-sidebar-foreground outline-none"
                )}
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                aria-label="Действия с проектом"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-52" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem className="gap-2" onSelect={props.onNewChat}>
                <SquarePen className="h-3.5 w-3.5" />
                Новый чат в проекте
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onSelect={props.onStartRename}>
                <Pencil className="h-3.5 w-3.5" />
                Переименовать
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive gap-2" onSelect={props.onDelete}>
                <Trash2 className="h-3.5 w-3.5" />
                Удалить проект
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  )
}

// ── Переименование на месте ─────────────────────────────────────────────────

function InlineRename({ initial, onSubmit, onCancel, label }: { initial: string; onSubmit: (v: string) => void; onCancel: () => void; label: string }) {
  const [value, setValue] = useState(initial)
  const doneRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    // Меню закрывается и возвращает фокус на кнопку — ставим фокус после него
    const t = setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select() }, 30)
    return () => clearTimeout(t)
  }, [])
  const finish = (save: boolean) => {
    if (doneRef.current) return
    doneRef.current = true
    if (save) onSubmit(value)
    else onCancel()
  }
  return (
    <input
      ref={inputRef}
      value={value}
      maxLength={200}
      aria-label={label}
      className="flex-1 min-w-0 h-7 rounded-md border border-sidebar-border bg-background px-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === "Enter") finish(true)
        if (e.key === "Escape") finish(false)
      }}
      onBlur={() => finish(true)}
    />
  )
}

// ── Новый проект ────────────────────────────────────────────────────────────

function ProjectFormDialog({ form, onClose, onSubmit }: { form: ProjectForm; onClose: () => void; onSubmit: (name: string) => Promise<boolean> }) {
  const [name, setName] = useState("")
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (form) setName("") }, [form])
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const clean = name.replace(/\s+/g, " ").trim()
    if (!clean || saving) return
    setSaving(true)
    const ok = await onSubmit(clean)
    setSaving(false)
    if (ok) onClose()
  }
  return (
    <Dialog open={!!form} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Новый проект</DialogTitle>
            <DialogDescription>
              {form?.moveDialogId ? "Папка для чатов; выбранный чат сразу переместится в неё." : "Папка, в которую можно складывать чаты по одной теме."}
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            maxLength={80}
            placeholder="Например, «Насосная станция №2»"
            onChange={(e) => setName(e.target.value)}
            aria-label="Название проекта"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Отмена</Button>
            <Button type="submit" disabled={!name.trim() || saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Создать проект
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
